import React from 'react';
import { useDispatch } from 'react-redux';
import { Box, Grid, GridItem, Text, Tooltip, VStack } from '@chakra-ui/react';

import PINSAIIcon from '../../../../../../components/PINSAIIcon';
import UserIcon from '../../../../../../components/UserIcon';
import { CypressIds } from '../../../../../../constants/cypressIds';
import { hideTray, showTray } from '../../../../../../redux/modules/tray';
import { globalRequirementTemplateBuilder } from '../../../../../../redux/reducer';
import colors from '../../../../../../styles/theme/foundation/colors';
import RequirementTemplateType from '../../../../../../types/RequirementTemplateType';
import { getDateString } from '../../../../../../utils/dateUtil';
import TemplateIconMenu from '../TemplateIconMenu';
import TemplatePolicyTypes from '../TemplatePolicyTypes';
import TemplatePreviewTray from '../TemplatePreviewTray';

const TemplateRow = ({ template }) => {
  const dispatch = useDispatch();

  const isArchived = !!template.archivedAt;
  const dateFormat = 'MM/DD/YYYY';
  let templateDateInfo;
  let layerStyle;

  if (isArchived) {
    templateDateInfo = `Archived - ${getDateString(template.archivedAt, dateFormat)}`;
    layerStyle = 'secondaryArchived';
  } else if (template?.updatedAt) {
    templateDateInfo = `Last Modified - ${getDateString(template.updatedAt, dateFormat)}`;
  } else if (template?.publishedAt) {
    templateDateInfo = `Published - ${getDateString(template.publishedAt, dateFormat)}`;
  } else {
    templateDateInfo = `Created - ${getDateString(template.createdAt, dateFormat)}`;
  }

  const showTemplatePreviewTray = () => {
    dispatch(globalRequirementTemplateBuilder.getRequirementTemplate(template.id));

    dispatch(showTray(() => <TemplatePreviewTray blueprint={globalRequirementTemplateBuilder} />, {
      size: 'md',
      onClose: () => {
        dispatch(globalRequirementTemplateBuilder.resetSelectedTemplate());
        dispatch(hideTray());
      }
    }));
  };

  return (
    <Box
      _hover={{ cursor: 'pointer', boxShadow: 'hover' }}
      borderRadius="md"
      boxShadow="0px 6px 18px rgba(123, 150, 178, 0.12)"
      data-cy={CypressIds.reqTemplatesGetTemplateRowByName(template.name)}
      layerStyle={layerStyle}
      margin="0 auto"
      mb={4}
      minHeight="187px"
      mt="2px"
      p={3}
      width="98%"
      onClick={showTemplatePreviewTray}
    >
      <Grid gap="15px" height="100%" templateColumns="repeat(12, 1fr)" templateRows="repeat(4, 1fr)" textAlign="left">
        <GridItem colSpan={1} margin="0 auto" rowSpan={4}>
          <UserIcon
            disabled={isArchived}
            firstName={template.creator?.userFirstName}
            fontSize="md"
            iconSize="40px"
            lastName={template.creator?.userLastName}
          />
        </GridItem>

        <GridItem colSpan={8}>
          <VStack alignItems="flex-start" spacing={0}>
            <Text textStyle="bodyLg600">{template.name}</Text>
            <Text color={colors.greyScale['500']} textStyle="bodySm">
              {templateDateInfo}
            </Text>
          </VStack>
        </GridItem>
        <GridItem colSpan={3}>
          {template.requirementTemplateQuestions?.length > 0 && (
            <Box m={2} textAlign="right">
              <Tooltip label="This template has PINS AI Assistant enabled.">
                <Box>
                  <PINSAIIcon iconBoxSize="30px" />
                </Box>
              </Tooltip>
            </Box>
          )}
        </GridItem>

        <GridItem
          alignItems="center"
          colSpan={10}
          display="flex"
          rowSpan={2}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <Text
              color={colors.greyScale['500']}
              overflow="hidden"
              textOverflow="ellipsis"
              textStyle="bodyMd"
            >
              {template.description || <i>No Description</i>}
            </Text>
          </span>
        </GridItem>

        <GridItem alignItems="center" colSpan={1} display="flex" justifyContent="center" rowSpan={2}>
          <TemplateIconMenu template={template} />
        </GridItem>

        <TemplatePolicyTypes templateRequirementBlocks={template.requirementTemplateBlocks} />
      </Grid>
    </Box>
  );
};

TemplateRow.propTypes = { template: RequirementTemplateType };

export default TemplateRow;
